// LeadMagnetMicro.js
// Vonzás 03 · Ügyfélmágnes Varázsló · 1. kártya — Mikromágnes.
//
// REUSE STRATÉGIA (additív — a meglévő ügynököket NEM módosítja):
//   - phase = "sketches": a MagnetPostFacebook sketch-fázisának 2-paneles
//     krém doc + lila refine sávjával IDENTIKUS (CreamSketchSection
//     komponens újrahasznosítva).
//   - phase = "result": az EmailValueAdd result-fázisának 2-paneles héja
//     (krém dokumentum + bal oldali finomító + kijelölés-alapú refine
//     SuggestionCard/AppliedBlock-kal), egyszerűsítve a Mikromágnes
//     kontent-shape-jére (NEM email tárgysor switch, hanem mikromágnes
//     body + külön kódblokk-szerű kézfeltartós poszt doboz).
//
// PROPS:
//   - initialSketches: a sketches endpointtól kapott 3 vázlat
//   - tema — a setup-ból átörökítve (a formátumot a sketch.format adja)
//   - valasztottTopicObject — a téma-pille teljes objektuma (ha pille-ből)

import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  CircleNotch, Copy as CopyIcon, DownloadSimple, CheckCircle,
  Paperclip, PaperPlaneTilt, X as XIcon, BookmarkSimple,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { runPdfExportJob, downloadBlobAsFile } from "../lib/pdfExportJob";
import { SuggestionCard, AppliedBlock } from "../components/AgentOutputScreen";
import { getActiveAlap } from "../lib/activeAlap";

const ACCENT_BLUE = "#26A9C9";
const ACCENT_BLUE_DARK = "#135b6c";

// ───────────────────────────────────────────────────────────────────────
// MikromagnesMarkdown — könnyű, prémium markdown renderer kifejezetten a
// Writer korlátozott markdown szótárához (##/###/-/1./bekezdés). Speciális
// szekciócímeket callout-dobozokká alakít (pl. „Ezt vidd magaddal" → kék
// positive callout, „Ezt ne írd" / „Ezt most ne csináld" → halvány warning
// callout). NEM jelenít meg nyers `**` vagy `---` jelet.
// ───────────────────────────────────────────────────────────────────────
const _CALLOUT_RULES = [
  { tone: "positive", patterns: [
    /^ezt vidd magaddal/i, /^mit vihetsz tovább/i, /^ha most csak egy dolgot teszel/i,
    /^ha csak egy dolgot javít/i, /^mi legyen a következő apró lépés/i,
    /^mi változhat ettől/i, /^mire figyelj a végén/i,
    /^ha szeretnéd ezt továbbvinni/i, /^ha kíváncsi vagy a folytatásra/i,
  ]},
  { tone: "warning", patterns: [
    /^ezt ne írd/i, /^ezt most ne csináld/i, /^mit ne csinálj/i,
  ]},
  { tone: "calm", patterns: [
    /^ha most ebben vagy/i,
  ]},
];

function _detectCalloutTone(heading) {
  const h = (heading || "").trim();
  for (const rule of _CALLOUT_RULES) {
    if (rule.patterns.some((rx) => rx.test(h))) return rule.tone;
  }
  return null;
}

function _renderInline(text, key) {
  if (!text) return null;
  const parts = [];
  let rest = text.replace(/\*\*\*\*/g, "**");
  let i = 0;
  while (rest.length > 0) {
    const m = rest.match(/\*\*([^*]+)\*\*/);
    if (!m) { parts.push(rest); break; }
    if (m.index > 0) parts.push(rest.slice(0, m.index));
    parts.push(<strong key={`b-${key}-${i++}`} style={{ fontWeight: 700, color: "#0a0a12" }}>{m[1]}</strong>);
    rest = rest.slice(m.index + m[0].length);
  }
  return parts;
}

function _MdBullet({ children }) {
  return (
    <li style={{
      position: "relative", paddingLeft: 22, marginBottom: 8,
      fontSize: 15, color: "#1a1a22", lineHeight: 1.65,
    }}>
      <span style={{
        position: "absolute", left: 4, top: 9,
        width: 6, height: 6, borderRadius: 3, background: "#26A9C9",
      }} />
      {children}
    </li>
  );
}

function _MdNumStep({ n, children }) {
  return (
    <div style={{
      display: "flex", alignItems: "flex-start", gap: 14,
      padding: "12px 14px", marginBottom: 10,
      background: "#ffffff",
      border: "1px solid rgba(38,169,201,0.22)",
      borderRadius: 10,
      boxShadow: "0 1px 2px rgba(10,10,18,0.03)",
    }}>
      <div style={{
        flexShrink: 0, width: 26, height: 26, borderRadius: "50%",
        background: "#26A9C9",
        color: "#ffffff",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 12, fontWeight: 700,
        boxShadow: "0 1px 2px rgba(38,169,201,0.40)",
      }}>{n}</div>
      <div style={{ flex: 1, fontSize: 15, color: "#1a1a22", lineHeight: 1.65 }}>
        {children}
      </div>
    </div>
  );
}

function _MdCallout({ tone, title, children }) {
  const palette = {
    positive: { bg: "#ECF7FA", border: "rgba(38,169,201,0.35)", label: "#135b6c", icon: "✓" },
    warning:  { bg: "#FFF5EC", border: "rgba(196,118,71,0.32)", label: "#7a3f1c", icon: "!" },
    calm:     { bg: "#F4F1FB", border: "rgba(124,108,242,0.28)", label: "#3f3590", icon: "·" },
  }[tone] || { bg: "#F7F4ED", border: "rgba(10,10,18,0.10)", label: "#333", icon: "·" };
  return (
    <div style={{
      background: palette.bg, border: `1px solid ${palette.border}`,
      borderRadius: 12, padding: "16px 18px", marginTop: 18, marginBottom: 14,
    }}>
      {title ? (
        <div style={{
          display: "flex", alignItems: "center", gap: 8,
          fontSize: 10.5, color: palette.label, fontWeight: 700,
          letterSpacing: "0.14em", textTransform: "uppercase",
          marginBottom: 10,
        }}>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 16, height: 16, borderRadius: 999,
            background: palette.border, color: "#fff", fontSize: 10, fontWeight: 700,
          }}>{palette.icon}</span>
          {title}
        </div>
      ) : null}
      <div style={{ fontSize: 15, color: "#1a1a22", lineHeight: 1.65 }}>
        {children}
      </div>
    </div>
  );
}

function _parseBlockChildren(lines, keyPrefix) {
  const out = [];
  let para = [];
  let bullets = [];
  let nums = [];
  const flushPara = () => {
    if (!para.length) return;
    const text = para.join(" ").trim();
    para = [];
    if (!text) return;
    out.push(
      <p key={`${keyPrefix}-p-${out.length}`} style={{
        fontSize: 15, color: "#1a1a22", lineHeight: 1.65, marginBottom: 10,
      }}>{_renderInline(text, `${keyPrefix}-p-${out.length}`)}</p>
    );
  };
  const flushBullets = () => {
    if (!bullets.length) return;
    out.push(
      <ul key={`${keyPrefix}-ul-${out.length}`} style={{ listStyle: "none", padding: 0, margin: "4px 0 12px" }}>
        {bullets.map((b, i) => (
          <_MdBullet key={`${keyPrefix}-li-${out.length}-${i}`}>{_renderInline(b, `${keyPrefix}-li-${out.length}-${i}`)}</_MdBullet>
        ))}
      </ul>
    );
    bullets = [];
  };
  const flushNums = () => {
    if (!nums.length) return;
    out.push(
      <div key={`${keyPrefix}-ol-${out.length}`} style={{ margin: "6px 0 14px" }}>
        {nums.map((entry, i) => (
          <_MdNumStep
            key={`${keyPrefix}-ns-${out.length}-${i}`}
            n={entry.n}
          >{_renderInline(entry.text, `${keyPrefix}-ns-${out.length}-${i}`)}</_MdNumStep>
        ))}
      </div>
    );
    nums = [];
  };
  const flushAll = () => { flushPara(); flushBullets(); flushNums(); };

  for (const raw of lines) {
    const ln = raw.replace(/\t/g, "  ");
    if (/^\s*-{3,}\s*$/.test(ln)) { flushAll(); continue; }
    if (/^\s*$/.test(ln)) { flushAll(); continue; }
    const numMatch = ln.match(/^\s*(\d+)\.\s+(.*)$/);
    if (numMatch) {
      flushPara(); flushBullets();
      // Az ACTUAL markdown-számot tároljuk (1,2,3…), hogy a kártyán a
      // tényleges sorszám jelenjen meg - akkor is, ha bekezdés/üres sor
      // szakítja meg a listát.
      nums.push({ n: parseInt(numMatch[1], 10), text: numMatch[2].trim() });
      continue;
    }
    const bulMatch = ln.match(/^\s*[-*]\s+(.*)$/);
    if (bulMatch) { flushPara(); flushNums(); bullets.push(bulMatch[1].trim()); continue; }
    flushBullets(); flushNums();
    para.push(ln.trim());
  }
  flushAll();
  return out;
}

function MikromagnesMarkdown({ source, format }) {
  const text = (source || "").trim();
  if (!text) return null;
  const lines = text.split(/\r?\n/);
  const sections = [];
  let cursor = { heading: null, lines: [] };
  for (const ln of lines) {
    const m = ln.match(/^###\s+(.+?)\s*$/);
    if (m) {
      if (cursor.heading != null || cursor.lines.length > 0) sections.push(cursor);
      cursor = { heading: m[1].trim().replace(/^[#*\s]+/, ""), lines: [] };
    } else if (ln.match(/^##\s+/)) {
      continue;
    } else {
      cursor.lines.push(ln);
    }
  }
  if (cursor.heading != null || cursor.lines.length > 0) sections.push(cursor);

  const blocks = [];
  sections.forEach((sec, sIdx) => {
    const tone = _detectCalloutTone(sec.heading);
    const items = _parseBlockChildren(sec.lines, `s${sIdx}`);
    if (tone) {
      blocks.push(
        <_MdCallout key={`c${sIdx}`} tone={tone} title={sec.heading}>
          {items}
        </_MdCallout>
      );
    } else {
      blocks.push(
        <div key={`s${sIdx}`} style={{ marginTop: sIdx === 0 ? 0 : 22, marginBottom: 6 }}>
          {sec.heading ? (
            <div style={{ marginBottom: 12 }}>
              <h3 style={{
                fontFamily: "inherit",
                fontSize: 15, fontWeight: 600, color: "#26A9C9",
                letterSpacing: "-0.005em", lineHeight: 1.35,
                margin: 0,
              }}>
                {sec.heading}
              </h3>
              {/* Finom kék elválasztó */}
              <div style={{
                marginTop: 8,
                height: 1,
                background: "linear-gradient(90deg, rgba(38,169,201,0.32), rgba(38,169,201,0))",
                width: 60,
              }} />
            </div>
          ) : null}
          {items}
        </div>
      );
    }
  });

  return (
    <div data-testid="lead-magnet-body" data-format={format || ""}>
      {blocks}
    </div>
  );
}

/**
 * `LeadMagnetSketchCard` — saját, mikromágnes-specifikus sketch render.
 * NEM a `CreamSketchSection`-t használjuk: a Mikromágnes vázlatkártyája az
 * új Vázlat-Generátor szigorúbb mezőit mutatja (fantázianév, formátum-tag,
 * preview, „Ezt kapja a végén", „Mikor jó?", „Miért fogja kérni?").
 */
function LeadMagnetSketchCard({ sketch, index, isLast, expanding, onClick }) {
  const [hover, setHover] = useState(false);
  const ACCENT = "#26A9C9";
  const fmt = (sketch.format || sketch.format_label || "").trim();
  const angle = (sketch.angle_type || sketch.szog || sketch.nezopont || "").trim();
  const title = (sketch.title || sketch.horog || "").trim();
  const preview = (sketch.preview || sketch.uj_nezopont || "").trim();
  const userLeavesWith = (sketch.user_leaves_with || "").trim();
  const whenToUse = (sketch.when_to_use || "").trim();
  const whyTheyWantIt = (sketch.why_they_want_it || "").trim();
  const formatPill = fmt ? (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "4px 11px", borderRadius: 999,
        background: "rgba(38,169,201,0.10)",
        border: "1px solid rgba(38,169,201,0.32)",
        color: "#135b6c",
        fontSize: 10.5, fontWeight: 600,
        letterSpacing: "0.10em", textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
      data-testid={`lead-magnet-sketch-format-${index}`}
    >
      {fmt}
    </span>
  ) : null;
  return (
    <div
      onClick={expanding ? undefined : onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      data-testid={`lead-magnet-sketch-${sketch.id || index}`}
      style={{
        cursor: expanding ? "wait" : "pointer",
        padding: "20px 0",
        borderBottom: isLast ? "none" : "1px solid rgba(10,10,18,0.08)",
        background: hover ? "rgba(38, 169, 201,0.06)" : "transparent",
        margin: hover ? "0 -16px" : "0",
        paddingLeft: hover ? 16 : 0,
        paddingRight: hover ? 16 : 0,
        borderRadius: hover ? 8 : 0,
        transition: "background 180ms ease",
      }}
    >
      <div
        style={{
          fontSize: 11, color: "#26A9C9", lineHeight: 1.6, marginBottom: 10,
          display: "flex", flexWrap: "wrap", gap: "2px 6px", alignItems: "center",
          letterSpacing: "0.20em", fontWeight: 600, textTransform: "uppercase",
        }}
      >
        {angle && <span>NÉZŐPONT: <span style={{ fontWeight: 600 }}>{angle}</span></span>}
        {angle && formatPill ? (
          <span style={{ padding: "0 7px", fontWeight: 700 }}>·</span>
        ) : null}
        {formatPill}
      </div>
      <h3
        className="font-heading"
        style={{
          fontSize: 19, fontWeight: 700, color: "#0a0a12",
          lineHeight: 1.3, marginBottom: 8, letterSpacing: "-0.012em",
        }}
        data-testid={`lead-magnet-sketch-title-${index}`}
      >
        {title}
      </h3>
      {preview ? (
        <p
          style={{
            fontSize: 14, color: "#4a4a55", lineHeight: 1.55, marginBottom: 14,
            fontWeight: 400,
          }}
          data-testid={`lead-magnet-sketch-preview-${index}`}
        >
          {preview}
        </p>
      ) : null}
      {(userLeavesWith || whenToUse || whyTheyWantIt) && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 14,
            marginBottom: 12,
          }}
        >
          {userLeavesWith ? (
            <div data-testid={`lead-magnet-sketch-leaves-with-${index}`}>
              <div style={{
                fontSize: 9, color: ACCENT_BLUE_DARK,
                letterSpacing: "0.16em", fontWeight: 700,
                textTransform: "uppercase", marginBottom: 4,
              }}>Ezt kapja a végén</div>
              <div style={{ fontSize: 13, color: "#2a2a35", lineHeight: 1.5 }}>{userLeavesWith}</div>
            </div>
          ) : null}
          {whenToUse ? (
            <div data-testid={`lead-magnet-sketch-when-${index}`}>
              <div style={{
                fontSize: 9, color: ACCENT_BLUE_DARK,
                letterSpacing: "0.16em", fontWeight: 700,
                textTransform: "uppercase", marginBottom: 4,
              }}>Mikor jó?</div>
              <div style={{ fontSize: 13, color: "#2a2a35", lineHeight: 1.5 }}>{whenToUse}</div>
            </div>
          ) : null}
          {whyTheyWantIt ? (
            <div data-testid={`lead-magnet-sketch-why-${index}`}>
              <div style={{
                fontSize: 9, color: ACCENT_BLUE_DARK,
                letterSpacing: "0.16em", fontWeight: 700,
                textTransform: "uppercase", marginBottom: 4,
              }}>Miért fogja kérni?</div>
              <div style={{ fontSize: 13, color: "#2a2a35", lineHeight: 1.5 }}>{whyTheyWantIt}</div>
            </div>
          ) : null}
        </div>
      )}
      <div
        style={{
          fontSize: 12.5, color: ACCENT, fontWeight: 600,
          display: "inline-flex", alignItems: "center", gap: 4,
        }}
      >
        {expanding ? (
          <><CircleNotch size={12} weight="bold" className="animate-spin" /> Kibontás…</>
        ) : (
          <>Ezt írd meg teljesen →</>
        )}
      </div>
    </div>
  );
}

const QUICK_REFINE_ACTIONS = [
  { id: "edge",     label: "Élesebb" },
  { id: "soft",     label: "Lágyabb" },
  { id: "concrete", label: "Konkrétabb" },
  { id: "story",    label: "Több sztori" },
  { id: "humor",    label: "Humorosabb" },
  { id: "surprise", label: "Lepj meg!" },
];

/**
 * `CreamActionPill` — INLINE MÁSOLAT a MagnetPostFacebook-belőli komponensből
 * (additív: az eredeti változatlan). Bájtról-bájtra megegyező stílus, hogy a
 * Mikromágnes eredmény-képernyő toolbar pillei pontosan ugyanúgy nézzenek ki.
 */
const POST_TEAL = "#26A9C9";
function CreamActionPill({ onClick, icon: Icon, label, testid, active, disabled, accent }) {
  const accentColor = accent || POST_TEAL;
  const accentBg = active ? "rgba(29,158,117,0.10)" : `${accentColor}14`;
  const accentBorder = active ? "rgba(29,158,117,0.30)" : `${accentColor}3d`;
  const accentText = active ? "#1d9e75" : "#135b6c";
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      data-testid={testid}
      disabled={disabled}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 12px",
        background: accentBg,
        border: `1px solid ${accentBorder}`,
        borderRadius: 999,
        color: accentText,
        fontSize: 12, fontWeight: 500,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        whiteSpace: "nowrap",
        fontFamily: "inherit",
        transition: "all 180ms ease",
      }}
    >
      <Icon size={13} weight="regular" /> {label}
    </button>
  );
}

/**
 * A Writer kimenetét két részre bontjuk a poszt eredmény-képernyőjéhez:
 *   - `title`: a vezető `## H2 cím` (poszt H1-stílussal renderelve)
 *   - `body`:  a maradék szövegtörzs (poszt pre-wrap törzsstílussal). A
 *              `#### blokkcím` markereket eltávolítjuk, de a sortöréseket
 *              megtartjuk — a blokkok így új-soros bekezdésekként
 *              jelennek meg, mint egy hosszabb posztnál.
 */
function splitWriterBody(raw) {
  const text = String(raw || "");
  const lines = text.split("\n");
  // A vezető `## ` sor a cím; ha nincs, üres címet adunk, és a teljes
  // szöveg a body.
  let title = "";
  let bodyStart = 0;
  // Vezető üres sorokat átugorjuk.
  while (bodyStart < lines.length && lines[bodyStart].trim() === "") bodyStart++;
  if (bodyStart < lines.length && lines[bodyStart].startsWith("## ")) {
    title = lines[bodyStart].replace(/^##\s+/, "").trim();
    bodyStart++;
    while (bodyStart < lines.length && lines[bodyStart].trim() === "") bodyStart++;
  }
  let body = lines.slice(bodyStart).join("\n");
  // A `#### Heading` sorokat sima szövegként kezeljük (a markert leszedjük,
  // a sort meghagyjuk — a poszt-style pre-wrap renderben ez plusz sortörés-
  // pár közé esik, vizuális szakaszhatárként). Esetleges `## ` sub-heading-ek
  // ugyanígy lemeztelenítve.
  body = body.replace(/^####\s+/gm, "").replace(/^##\s+/gm, "");
  return { title: title.trim(), body: body.replace(/^\n+/, "").replace(/\n+$/, "") };
}

/** Központi cím-kiválasztó: a Writer `##` címe → mikro.title → fallback. */
function mikroTitleFromContent(content) {
  const mk = content?.mikromagnes || {};
  const { title } = splitWriterBody(mk.body || "");
  return (title && title.trim()) || (mk.title || "").trim();
}

export default function LeadMagnetMicro({
  initialSketches = [],
  tema = "",
  valasztottTopicObject = null,
  reportClosingMode = "clean_close",
}) {
  // phase: sketches | result
  const [phase, setPhase] = useState("sketches");
  const [vazlatok, setVazlatok] = useState(initialSketches);
  const [chosenSketch, setChosenSketch] = useState(null);
  const [expanding, setExpanding] = useState(false);

  // Sketch-fázis re-roll state
  const [sketchBusy, setSketchBusy] = useState(false);
  const [sketchChat, setSketchChat] = useState("");
  const [seenSketchTitles, setSeenSketchTitles] = useState(
    () => (initialSketches || []).map((v) => (v.title || v.horog || "").trim()).filter(Boolean)
  );

  // Result state — leadmagnet_content: { mikromagnes, kezfeltartos_poszt }
  const [content, setContent] = useState(null);
  const [rawText, setRawText] = useState("");

  // Másolás / mentés UI állapot
  const [copied, setCopied] = useState(false);
  const [copiedPost, setCopiedPost] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Refine state
  const [selection, setSelection] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [refinePhase, setRefinePhase] = useState("idle"); // idle | loading | suggestion | applied
  const [suggestion, setSuggestion] = useState(null);
  const [suggestionError, setSuggestionError] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [undoSnapshot, setUndoSnapshot] = useState(null);
  const undoTimerRef = useRef(null);
  const previewRef = useRef(null);

  const activeAlap = useMemo(() => getActiveAlap(), []);

  // ── Step: Pick sketch → /attraction/lead-magnet/full ─────────────
  const handleExpand = async (sketch) => {
    if (expanding) return;
    setExpanding(true);
    setChosenSketch(sketch);
    try {
      const res = await api.post("/attraction/lead-magnet/full", {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        tema: (tema || "").trim(),
        valasztott_vazlat: sketch,
        report_closing_mode: reportClosingMode || "clean_close",
      });
      const txt = (res.data?.teljes_szoveg || "").trim();
      const lc = res.data?.leadmagnet_content || null;
      if (!txt || !lc) {
        toast.error("Üres válasz. Próbáld újra.");
        return;
      }
      setRawText(txt);
      setContent(lc);
      setPhase("result");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "A mikromágnes generálása sikertelen.");
    } finally {
      setExpanding(false);
    }
  };

  // ── Sketch re-roll ───────────────────────────────────────────────
  const regenerateSketches = async (instruction) => {
    if (sketchBusy) return;
    setSketchBusy(true);
    try {
      const res = await api.post("/attraction/lead-magnet/sketches", {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        tema: (tema || "").trim(),
        topic_object: valasztottTopicObject || null,
        user_instruction: instruction || "",
        previous_sketch_titles: seenSketchTitles,
      });
      const vs = Array.isArray(res.data?.vazlatok) ? res.data.vazlatok : [];
      if (!vs.length) {
        toast.error("Nem érkezett új vázlat. Próbáld újra.");
        return;
      }
      setVazlatok(vs);
      setSeenSketchTitles((prev) => Array.from(new Set([
        ...prev,
        ...vs.map((v) => (v.title || v.horog || "").trim()).filter(Boolean),
      ])));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Új vázlatok lekérése sikertelen.");
    } finally {
      setSketchBusy(false);
    }
  };

  // ── Composed full text — rawText az új szöveg-mester. Refine után
  //    a rawText-be írunk, a render azt olvassa.
  const composedAllText = rawText || "";

  // ── Selection capture (csak a mikromágnes body-ban — a kódblokk-poszt
  //    `user-select: text`, de a refine csak a body szövegre vonatkozik)
  useEffect(() => {
    if (phase !== "result") return undefined;
    const handleMouseUp = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      const text = sel.toString().trim();
      if (text.length < 3) return;
      const root = previewRef.current;
      if (!root) return;
      let node = sel.anchorNode;
      while (node && node !== root) node = node.parentNode;
      if (!node) return;
      setSelection(text.slice(0, 600));
    };
    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [phase]);

  const clearSelection = () => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  // ── Refine pipeline ──────────────────────────────────────────────
  const requestRefine = async (instructionOverride, quickButtonId = null) => {
    const utasitas = (instructionOverride || chatInput).trim();
    if (!utasitas && !quickButtonId) return;
    if (refinePhase === "loading" || refinePhase === "suggestion") return;
    const quoteToSend = selection;
    setSuggestionError(false);
    setRefinePhase("loading");
    if (!instructionOverride) setChatInput("");
    try {
      const res = await api.post("/attraction/lead-magnet/refine", {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        tema: (tema || "").trim(),
        valasztott_vazlat: chosenSketch || null,
        jelenlegi_sorozat: composedAllText,
        utasitas: utasitas || "",
        quote: quoteToSend || null,
        quick_button: quickButtonId || null,
      });
      const data = res.data || {};
      const mode = data?.metadata?.mode === "snippet" ? "snippet" : "teljes";
      setSuggestion({
        mod: mode,
        snippet: data.snippet || "",
        teljes: data.teljes_szoveg || "",
        content: data.leadmagnet_content || null,
        instr: utasitas || (quickButtonId || ""),
        quote: quoteToSend || null,
      });
      setRefinePhase("suggestion");
    } catch {
      setSuggestion({
        mod: "error",
        instr: utasitas || (quickButtonId || ""),
        quote: quoteToSend || null,
      });
      setSuggestionError(true);
      setRefinePhase("suggestion");
    }
  };

  const retryVariant = async () => {
    if (!suggestion || regenerating) return;
    const previousSnippet =
      suggestion.mod === "snippet" ? (suggestion.snippet || "") : (suggestion.teljes || "");
    setRegenerating(true);
    setSuggestionError(false);
    try {
      const res = await api.post("/attraction/lead-magnet/refine", {
        voice_id: activeAlap.voice,
        client_id: activeAlap.client,
        offer_id: activeAlap.offer,
        tema: (tema || "").trim(),
        valasztott_vazlat: chosenSketch || null,
        jelenlegi_sorozat: composedAllText,
        utasitas: (suggestion.instr || "") +
          "\n\nGenerálj egy érdemben MÁSIK változatot, mint az előzőt." +
          (previousSnippet ? ` Az előző javaslat eleje így szólt: "${previousSnippet.slice(0, 280)}". Kerüld el az ismétlést.` : ""),
        quote: suggestion.quote || null,
      });
      const data = res.data || {};
      const mode = data?.metadata?.mode === "snippet" ? "snippet" : "teljes";
      setSuggestion((s) => ({
        ...s, mod: mode,
        snippet: data.snippet || "",
        teljes: data.teljes_szoveg || "",
        content: data.leadmagnet_content || null,
      }));
    } catch {
      setSuggestionError(true);
    } finally {
      setRegenerating(false);
    }
  };

  const retryAfterError = () => {
    if (!suggestion) return;
    setSuggestionError(false);
    requestRefine(suggestion.instr);
  };

  const cancelSuggestion = () => {
    setSuggestion(null);
    setSuggestionError(false);
    setRefinePhase("idle");
  };

  const applySuggestion = () => {
    if (!suggestion) return;
    const prevText = composedAllText;
    let nextText = prevText;
    if (suggestion.mod === "snippet" && suggestion.snippet && suggestion.quote) {
      const idx = prevText.indexOf(suggestion.quote);
      if (idx >= 0) {
        nextText = prevText.slice(0, idx) + suggestion.snippet + prevText.slice(idx + suggestion.quote.length);
      } else {
        setRefinePhase("idle"); setSuggestion(null); return;
      }
    } else if (suggestion.mod === "teljes" && suggestion.teljes) {
      nextText = suggestion.teljes;
    } else {
      setRefinePhase("idle"); setSuggestion(null); return;
    }
    setUndoSnapshot({ content, rawText });
    setRawText(nextText);
    // Snippet módban a strukturált content-et is megpróbáljuk frissíteni.
    if (suggestion.mod === "snippet" && content) {
      const nextContent = JSON.parse(JSON.stringify(content));
      const mikroBody = nextContent?.mikromagnes?.body || "";
      const postBody = nextContent?.kezfeltartos_poszt?.body || "";
      if (mikroBody.includes(suggestion.quote)) {
        nextContent.mikromagnes.body = mikroBody.replace(suggestion.quote, suggestion.snippet);
      } else if (postBody.includes(suggestion.quote)) {
        nextContent.kezfeltartos_poszt.body = postBody.replace(suggestion.quote, suggestion.snippet);
      }
      setContent(nextContent);
    } else if (suggestion.mod === "teljes" && suggestion.content) {
      setContent(suggestion.content);
    }
    setSelection(null);
    setSaved(false);
    window.getSelection()?.removeAllRanges();
    setTimeout(() => { try { window.getSelection()?.removeAllRanges(); } catch { /* noop */ } }, 0);
    setSuggestion(null);
    setRefinePhase("applied");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    undoTimerRef.current = setTimeout(() => {
      setRefinePhase("idle"); setUndoSnapshot(null);
    }, 8000);
  };

  const undoApplied = () => {
    if (!undoSnapshot) return;
    setContent(undoSnapshot.content);
    setRawText(undoSnapshot.rawText);
    setUndoSnapshot(null);
    setRefinePhase("idle");
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  };

  useEffect(() => () => {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
  }, []);

  const handleSubmitChat = () => requestRefine();

  // ── Másolás / letöltés / mentés ─────────────────────────────────
  const handleCopyAll = async () => {
    if (!composedAllText) return;
    try {
      await navigator.clipboard.writeText(composedAllText);
      setCopied(true); setTimeout(() => setCopied(false), 1800);
      toast.success("Vágólapra másolva");
    } catch { toast.error("Másolás nem sikerült"); }
  };
  const handleCopyPost = async () => {
    const body = content?.kezfeltartos_poszt?.body || "";
    if (!body) return;
    try {
      await navigator.clipboard.writeText(body);
      setCopiedPost(true); setTimeout(() => setCopiedPost(false), 1800);
      toast.success("Poszt másolva");
    } catch { toast.error("Másolás nem sikerült"); }
  };
  const handleDownload = async () => {
    if (!content) return;
    try {
      const title = mikroTitleFromContent(content) || "Mikromágnes";
      const fileBase = title
        .toLowerCase().normalize("NFKD").replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 64) || "mikromagnes";
      // Szerveroldali Playwright PDF — a blueprintekkel KÖZÖS pipeline-on
      // megy (krém éltől élig, @page margin:0, print-color-adjust:exact,
      // a TÉNYLEGES UI-riport rendereléssel a /print/lead-magnet route-ról).
      // A backend rövid életű draft-ot ment, betölti Playwright-tel,
      // PDF-et generál, draft-ot törli.
      // Async job flow — a szinkron endpoint 60s+ generálás esetén
      // 504 timeout-ra futna. A start endpoint egy draft-ot ment a
      // Mongoban és job_id-t ad; utána pollolunk és letöltünk.
      const blob = await runPdfExportJob({
        start: () =>
          api.post("/export/pdf/lead-magnet/start", {
            title,
            mikromagnes_body: content?.mikromagnes?.body || "",
            format_label: content?.mikromagnes?.format_label || content?.mikromagnes?.format || "",
            nezopont: content?.mikromagnes?.angle_type || content?.mikromagnes?.szog || "",
            tema: (tema || "").trim(),
            filename: `${fileBase}.pdf`,
          }),
      });
      downloadBlobAsFile(blob, `${fileBase}.pdf`);
      toast.success("PDF letöltve");
    } catch (e) {
      console.error(e);
      const detail = e?.response?.data?.detail || e?.message || "Ismeretlen hiba";
      toast.error("PDF letöltés nem sikerült: " + detail);
    }
  };
  const handleSaveToVault = async () => {
    if (!content?.mikromagnes?.body) return;
    if (saving) return;
    setSaving(true);
    try {
      const alap = getActiveAlap();
      await api.post("/attraction/lead-magnet/save", {
        name: mikroTitleFromContent(content) || "Mikromágnes",
        tema: (tema || "").trim(),
        topic_object: valasztottTopicObject || null,
        valasztott_vazlat: chosenSketch || null,
        format_id: content?.mikromagnes?.format_id || "",
        format_label: content?.mikromagnes?.format_label || content?.mikromagnes?.format || "",
        nezopont: content?.mikromagnes?.angle_type || content?.mikromagnes?.szog || "",
        angle_type: content?.mikromagnes?.angle_type || content?.mikromagnes?.szog || "",
        mikromagnes_body: content?.mikromagnes?.body || "",
        kezfeltartos_poszt: content?.kezfeltartos_poszt?.body || "",
        voice_id: alap?.voice || null,
        client_id: alap?.client || null,
        offer_id: alap?.offer || null,
      });
      setSaved(true);
      toast.success("Elmentve a Tudástárba");
      setTimeout(() => setSaved(false), 2200);
    } catch (e) {
      console.error(e);
      toast.error("Mentés nem sikerült");
    } finally {
      setSaving(false);
    }
  };

  const isMac = typeof navigator !== "undefined" && /Mac/i.test(navigator.platform);
  const isLoading = refinePhase === "loading";
  const showSuggestion = refinePhase === "suggestion" && suggestion;
  const showApplied = refinePhase === "applied";
  const inputDisabled = isLoading || showSuggestion;

  // ─────────────── PHASE: SKETCHES ─────────────────────────────────
  if (phase === "sketches") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        className="flex flex-col h-full"
        style={{ background: "#0a0a12" }}
        data-testid="lead-magnet-shell"
      >
        <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] flex-1 min-h-0 overflow-hidden">
          {/* LEFT — sketch refine panel */}
          <div className="flex flex-col border-r min-h-0 h-full"
               style={{ borderColor: "rgba(255,255,255,0.04)" }}
               data-testid="lead-magnet-sketches-finomito">
            <div className="flex-1 overflow-y-auto" style={{ padding: "32px 28px 14px" }}>
              <div style={{ maxWidth: 480 }}>
                <div className="font-heading"
                     style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.3,
                              letterSpacing: "-0.015em", color: "#ffffff", marginBottom: 18 }}>
                  Válassz egy vázlatot.
                </div>
                <div style={{ fontSize: 17, fontWeight: 400, lineHeight: 1.7, color: "#c0c0d0" }}>
                  Három különböző szögből és formátumból építettünk vázlatot. Kattints a kártyára jobboldalt — abból írom meg a teljes mikromágnest. Ha más szögből szeretnéd, kérd lent.
                </div>
              </div>
              {sketchBusy && (
                <div className="flex items-center gap-2.5"
                     style={{ fontSize: 13, color: "#a8a8c0", padding: "4px 2px", marginTop: 32 }}
                     data-testid="lead-magnet-sketches-busy">
                  <CircleNotch size={14} weight="bold" className="animate-spin" />
                  Új vázlatok készülnek…
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (sketchChat.trim() && !sketchBusy) {
                  regenerateSketches(sketchChat.trim());
                  setSketchChat("");
                }
              }}
              style={{ padding: "12px 24px 24px" }}
              data-testid="lead-magnet-sketches-form"
            >
              <div style={{
                background: "linear-gradient(180deg, rgba(243,238,228,0.06) 0%, rgba(243,238,228,0.02) 100%)",
                border: "1px solid rgba(243,238,228,0.14)",
                borderRadius: 12, padding: "5px 5px 5px 14px",
                display: "flex", alignItems: "center", gap: 10,
                boxShadow: "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
                opacity: sketchBusy ? 0.5 : 1,
                pointerEvents: sketchBusy ? "none" : "auto",
              }}>
                <button type="button" aria-label="Csatolás"
                        style={{ background: "transparent", border: "none", padding: 0,
                                 color: "#8a8a9c", cursor: "pointer",
                                 display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                  <Paperclip size={14} weight="regular" />
                </button>
                <input
                  value={sketchChat}
                  onChange={(e) => setSketchChat(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      if (sketchChat.trim() && !sketchBusy) {
                        regenerateSketches(sketchChat.trim()); setSketchChat("");
                      }
                    }
                  }}
                  placeholder={`Pl.: „adj 3 vázlatot, ami konkrét helyzetből indul"`}
                  data-testid="lead-magnet-sketches-chat"
                  disabled={sketchBusy}
                  className="flex-1 outline-none"
                  style={{ background: "transparent", border: "none", color: "white",
                           fontSize: 14, padding: "8px 0", fontFamily: "inherit" }}
                />
                <button type="submit"
                        disabled={!sketchChat.trim() || sketchBusy}
                        data-testid="lead-magnet-sketches-send"
                        aria-label="Új vázlatok"
                        style={{ width: 36, height: 36,
                                 background: "linear-gradient(135deg, #a89bff, #7c6cf2)",
                                 color: "white", borderRadius: 9, border: "none",
                                 display: "flex", alignItems: "center", justifyContent: "center",
                                 cursor: sketchChat.trim() && !sketchBusy ? "pointer" : "not-allowed",
                                 opacity: sketchChat.trim() && !sketchBusy ? 1 : 0.5,
                                 boxShadow: sketchChat.trim() && !sketchBusy
                                   ? "0 4px 12px rgba(124,108,242,0.45), inset 0 0 0 1px rgba(255,255,255,0.10)"
                                   : "none" }}>
                  {sketchBusy ? (
                    <CircleNotch size={15} weight="bold" className="animate-spin" />
                  ) : (
                    <PaperPlaneTilt size={15} weight="fill" />
                  )}
                </button>
              </div>
              <div className="flex justify-between" style={{ marginTop: 8, fontSize: 10.5, color: "#5a5a72" }}>
                <span>Vagy egyszerűen kattints a jobboldali vázlatok közül egyre.</span>
                <span>{isMac ? "⌘" : "Ctrl"} + Enter</span>
              </div>
            </form>
          </div>

          {/* RIGHT — krém doc CreamSketchSection-ökkel */}
          <div className="overflow-y-auto h-full"
               style={{ background: "#0a0a12", padding: "28px 36px 56px 36px" }}>
            <div
              data-preview-theme="cream"
              data-testid="lead-magnet-sketches-cream"
              className="mr-auto"
              style={{
                background: "#f3eee4", color: "#0a0a12", borderRadius: 10,
                boxShadow: "0 16px 50px -22px rgba(0,0,0,0.50), 0 0 0 1px rgba(255,255,255,0.03)",
                padding: "32px 44px 56px", maxWidth: 860, marginLeft: 0,
                position: "relative",
                fontFamily: "Inter, system-ui, -apple-system, 'Segoe UI', sans-serif",
              }}
            >
              <h2 className="font-heading"
                  style={{ fontSize: 26, fontWeight: 700, color: "#0a0a12",
                           letterSpacing: "-0.018em", marginBottom: 10, lineHeight: 1.2 }}>
                Mikromágnes vázlatok
              </h2>
              <p style={{ fontSize: 14, color: "#6a6a78", lineHeight: 1.6, marginBottom: 22, fontWeight: 400 }}>
                Három különböző szögből és formátumból építettünk vázlatot ehhez a témához. Válaszd ki, melyiket szeretnéd kész mikromágnessé írni. Kattints a kártyára.
              </p>
              <div data-testid="lead-magnet-sketches">
                {vazlatok.map((v, idx) => (
                  <LeadMagnetSketchCard
                    key={v.id || idx}
                    sketch={v}
                    index={idx}
                    isLast={idx === vazlatok.length - 1}
                    expanding={expanding && chosenSketch?.id === v.id}
                    onClick={() => handleExpand(v)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // ─────────────── PHASE: RESULT ───────────────────────────────────
  const placeholder = selection
    ? "Mit változtassak a kijelölt részen?"
    : "Mit szeretnél finomítani?";
  const suggestionSnippet = suggestion
    ? (suggestion.mod === "snippet" ? (suggestion.snippet || "") : (suggestion.teljes || ""))
    : "";

  const mikro = content?.mikromagnes || {};
  const poszt = content?.kezfeltartos_poszt || {};
  // Privát átadó üzenet KIVÉVE - a Mikromágnes Varázslóban már nem létezik.

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
      className="flex flex-col h-full"
      style={{ background: "#0a0a12" }}
      data-testid="lead-magnet-shell-result"
    >
      <div className="grid grid-cols-1 lg:grid-cols-[440px_1fr] flex-1 min-h-0 overflow-hidden">
        {/* LEFT — finomító panel */}
        <div className="flex flex-col border-r min-h-0 h-full"
             style={{ borderColor: "rgba(255,255,255,0.04)" }}
             data-testid="lead-magnet-finomito">
          <div className="flex-1 overflow-y-auto" style={{ padding: "32px 28px 14px" }}>
            <div style={{ maxWidth: 480 }}>
              <div className="font-heading"
                   style={{ fontSize: 28, fontWeight: 600, lineHeight: 1.3,
                            letterSpacing: "-0.015em", color: "#ffffff", marginBottom: 18 }}>
                Elkészült a mikromágnes.
              </div>
              <div style={{ fontSize: 17, fontWeight: 400, lineHeight: 1.7, color: "#c0c0d0" }}>
                Olvasd át jobboldalt. A mikromágnes alatt találod a kézfeltartós posztot is. Ha valamit finomítanál, jelölj ki egy részt a szövegben, vagy írd lent, mit változtassak.
              </div>
            </div>

            <AnimatePresence>
              {(selection || isLoading || showSuggestion) && (
                <motion.div
                  key="workspace"
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  style={{
                    background: "rgba(124,108,242,0.025)",
                    border: "1px solid rgba(124,108,242,0.10)",
                    borderRadius: 12, padding: 14, margin: "32px -8px 0",
                  }}
                  data-testid="lead-magnet-selection-workspace"
                >
                  {(selection || (suggestion && suggestion.quote)) && (
                    <div style={{
                      background: "rgba(255,255,255,0.04)",
                      borderLeft: "2px solid rgba(124,108,242,0.40)",
                      borderRadius: 8, padding: "11px 13px", marginBottom: 12,
                    }}>
                      <div className="flex items-start gap-2.5">
                        <div className="flex-1 min-w-0">
                          <div style={{ fontSize: 9, textTransform: "uppercase", letterSpacing: "0.16em",
                                        color: "#8a8a9c", fontWeight: 600, marginBottom: 4 }}>KIJELÖLT RÉSZ</div>
                          <div style={{ fontSize: 12.5, color: "#c0c0d0", lineHeight: 1.5,
                                        display: "-webkit-box", WebkitLineClamp: 3,
                                        WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                            {(() => {
                              const q = selection || suggestion?.quote || "";
                              return `„${q.length > 240 ? q.slice(0, 240) + "…" : q}"`;
                            })()}
                          </div>
                        </div>
                        {!showSuggestion && !isLoading && (
                          <button type="button" onClick={clearSelection}
                                  aria-label="Kijelölés törlése"
                                  style={{ width: 22, height: 22, borderRadius: 5,
                                           background: "rgba(255,255,255,0.04)",
                                           border: "1px solid rgba(255,255,255,0.08)",
                                           color: "#9898ad", cursor: "pointer",
                                           display: "inline-flex", alignItems: "center", justifyContent: "center",
                                           flexShrink: 0 }}
                                  data-testid="lead-magnet-selection-clear">
                            <XIcon size={11} weight="bold" />
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {!isLoading && !showSuggestion && selection && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5" data-testid="lead-magnet-quick-actions">
                      {QUICK_REFINE_ACTIONS.map((qa) => (
                        <button key={qa.id} type="button"
                                onClick={() => requestRefine(null, qa.id)}
                                data-testid={`lead-magnet-quick-${qa.id}`}
                                style={{ fontSize: 13, padding: "10px 14px", borderRadius: 999,
                                         background: "rgba(124,108,242,0.10)",
                                         border: "1px solid rgba(124,108,242,0.28)",
                                         color: "#cfc8ff", cursor: "pointer",
                                         fontFamily: "inherit", fontWeight: 500,
                                         whiteSpace: "nowrap", textAlign: "center", width: "100%" }}>
                          {qa.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {isLoading && (
                    <div className="flex items-center gap-2.5"
                         style={{ fontSize: 13, color: "#a8a8c0", padding: "4px 2px" }}
                         data-testid="lead-magnet-refine-loading">
                      <CircleNotch size={14} weight="bold" className="animate-spin" />
                      Finomítás folyamatban…
                    </div>
                  )}

                  {showSuggestion && (
                    <div data-testid="lead-magnet-suggestion">
                      <SuggestionCard
                        snippet={suggestionSnippet}
                        isError={suggestionError}
                        regenerating={regenerating}
                        onApply={applySuggestion}
                        onRetryVariant={retryVariant}
                        onRetryAfterError={retryAfterError}
                        onCancel={cancelSuggestion}
                      />
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showApplied && (
                <motion.div
                  key="applied"
                  initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  style={{ margin: "24px 0 0" }}
                  data-testid="lead-magnet-applied"
                >
                  <AppliedBlock onUndo={undoApplied} canUndo={!!undoSnapshot} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (chatInput.trim() && !inputDisabled) handleSubmitChat();
            }}
            style={{ padding: "12px 24px 24px" }}
            data-testid="lead-magnet-finomito-form"
          >
            <div style={{
              background: "linear-gradient(180deg, rgba(243,238,228,0.06) 0%, rgba(243,238,228,0.02) 100%)",
              border: "1px solid rgba(243,238,228,0.14)",
              borderRadius: 12, padding: "5px 5px 5px 14px",
              display: "flex", alignItems: "center", gap: 10,
              boxShadow: "0 4px 16px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.06)",
              opacity: inputDisabled ? 0.5 : 1,
              pointerEvents: inputDisabled ? "none" : "auto",
            }}>
              <button type="button" aria-label="Csatolás"
                      style={{ background: "transparent", border: "none", padding: 0,
                               color: "#8a8a9c", cursor: "pointer",
                               display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                <Paperclip size={14} weight="regular" />
              </button>
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    if (chatInput.trim() && !inputDisabled) handleSubmitChat();
                  }
                }}
                placeholder={placeholder}
                data-testid="lead-magnet-finomito-input"
                disabled={inputDisabled}
                className="flex-1 outline-none"
                style={{ background: "transparent", border: "none", color: "white",
                         fontSize: 14, padding: "8px 0", fontFamily: "inherit" }}
              />
              <button type="submit"
                      disabled={!chatInput.trim() || inputDisabled}
                      data-testid="lead-magnet-finomito-send"
                      aria-label="Finomítás indítása"
                      style={{ width: 36, height: 36,
                               background: "linear-gradient(135deg, #a89bff, #7c6cf2)",
                               color: "white", borderRadius: 9, border: "none",
                               display: "flex", alignItems: "center", justifyContent: "center",
                               cursor: chatInput.trim() && !inputDisabled ? "pointer" : "not-allowed",
                               opacity: chatInput.trim() && !inputDisabled ? 1 : 0.5,
                               boxShadow: chatInput.trim() && !inputDisabled
                                 ? "0 4px 12px rgba(124,108,242,0.45), inset 0 0 0 1px rgba(255,255,255,0.10)"
                                 : "none" }}>
                {isLoading ? (
                  <CircleNotch size={15} weight="bold" className="animate-spin" />
                ) : (
                  <PaperPlaneTilt size={15} weight="fill" />
                )}
              </button>
            </div>
            <div className="flex justify-between" style={{ marginTop: 8, fontSize: 10.5, color: "#5a5a72" }}>
              <span>Vagy jelölj ki egy szakaszt jobboldalt.</span>
              <span>{isMac ? "⌘" : "Ctrl"} + Enter</span>
            </div>
          </form>
        </div>

        {/* RIGHT — krém doc: mikromágnes + kódblokk-szerű kézfeltartós poszt */}
        <div className="overflow-y-auto h-full"
             style={{ background: "#0a0a12", padding: "28px 36px 56px 36px" }}>
          <div
            ref={previewRef}
            data-preview-theme="cream"
            data-testid="lead-magnet-result-cream"
            className="mr-auto cream-selectable"
            style={{
              // BÁJTRÓL-BÁJTRA azonos a MagnetPostFacebook cream-card-jával
              // (lásd post-fb-cream-card style block). Ne állíts itt eltérő
              // értéket — ha bármi módosul, a poszt verziót is kell követni.
              background: "#f3eee4",
              color: "#0a0a12",
              borderRadius: 10,
              boxShadow: "0 16px 50px -22px rgba(0,0,0,0.50), 0 0 0 1px rgba(255,255,255,0.03)",
              padding: "32px 44px 56px",
              maxWidth: 860,
              marginLeft: 0,
              position: "relative",
            }}
          >
            {/* Top-right action pill toolbar — azonos a poszt-cream-card
                toolbar-ával (Másolás / Letöltés). „Mentés a Tudástárba" pillét
                a Mikromágnesre most nem kötünk be külön — a poszt UI-ban is
                opcionális. */}
            <div
              className="flex justify-end items-center"
              style={{ gap: 6, marginBottom: 28, position: "relative", zIndex: 1 }}
              data-html2canvas-ignore="true"
              data-export="exclude"
            >
              <CreamActionPill
                onClick={handleCopyAll}
                icon={copied ? CheckCircle : CopyIcon}
                label={copied ? "Másolva" : "Másolás"}
                testid="lead-magnet-copy-all"
                disabled={!mikro.body}
              />
              <CreamActionPill
                onClick={handleDownload}
                icon={DownloadSimple}
                label="Letöltés"
                testid="lead-magnet-download"
                disabled={!mikro.body}
              />
              <CreamActionPill
                onClick={handleSaveToVault}
                icon={saved ? CheckCircle : BookmarkSimple}
                label={saving ? "Mentés…" : (saved ? "Elmentve" : "Mentés a Tudástárba")}
                testid="lead-magnet-save"
                disabled={!mikro.body || saving}
              />
            </div>

            {/* H1 cím + meta sor + cyan border — IDENTIKUS a poszt
                eredmény-képernyővel (post-metadata block). */}
            {(() => {
              const { title: writerTitle, body: writerBody } = splitWriterBody(mikro.body || "");
              const headerTitle = writerTitle || mikro.title || (tema ? `${tema} — Mikromágnes` : "Mikromágnes");
              const angleType = mikro.angle_type || mikro.szog || "";
              const fmt = mikro.format_label || mikro.format || "";
              return (
                <>
                  <div
                    style={{
                      marginTop: 48, marginBottom: 36, paddingBottom: 2,
                      borderBottom: "0.5px solid #26A9C9",
                    }}
                    data-testid="lead-magnet-metadata"
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "space-between",
                        gap: 16,
                        marginBottom: 26,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {/* Riport-eyebrow (Avatar/Ajánlat riport-világgal konzisztens) */}
                        <div style={{
                          fontSize: 10, color: "#26A9C9",
                          letterSpacing: "0.20em", fontWeight: 700,
                          textTransform: "uppercase", marginBottom: 8,
                        }}>
                          MEX Magic · Mikromágnes
                        </div>
                        <div
                          style={{
                            fontSize: 24, fontWeight: 600, color: "#0a0a12",
                            letterSpacing: "-0.012em", lineHeight: 1.22,
                          }}
                          data-testid="lead-magnet-title-h1"
                        >
                          {headerTitle}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "none" }} data-testid="lead-magnet-metadata">
                      {/* META SOR ELTÁVOLÍTVA a riportból a user kérése szerint.
                          A téma / nézőpont / formátum továbbra is backend adat,
                          de NEM user-facing. A data-testid megőrződik a tesztek
                          kompatibilitása miatt. */}
                    </div>
                  </div>

                  {/* Body — strukturált markdown rendering, prémium light style:
                      ##/### → szekciók, listák → bullet/lépéskártyák, speciális
                      szekciócímek (Ezt vidd magaddal / Ezt ne írd stb.) →
                      callout dobozok. NINCS nyers markdown jel a képernyőn. */}
                  <MikromagnesMarkdown source={writerBody} format={fmt} />
                </>
              );
            })()}

            {/* KÉZFELTARTÓS POSZT — CSAK webes nézet. PDF export `data-export="exclude"`
                miatt kihagyja. A copy IKON a kártya JOBB ALSÓ sarkában (a felső
                action-toolbarban már nincs külön „Másolás" gomb). */}
            <div style={{ height: 1, background: "rgba(10,10,18,0.10)", margin: "40px 0 22px" }} className="handraiser-block" data-export="exclude" />
            <section
              className="handraiser-block"
              data-export="exclude"
              data-testid="lead-magnet-handraiser-section"
            >
              <div style={{
                fontSize: 10, color: "#26A9C9", letterSpacing: "0.14em",
                fontWeight: 600, textTransform: "uppercase",
                marginBottom: 12,
              }}>
                Kézfeltartós poszt
              </div>
              <div
                style={{
                  position: "relative",
                  background: "#ffffff",
                  color: "#1a1a22",
                  border: "1px solid rgba(38,169,201,0.20)",
                  borderRadius: 12,
                  padding: "20px 22px 44px 22px",
                  fontSize: 15, lineHeight: 1.7,
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                  boxShadow: "0 2px 10px -4px rgba(10,10,18,0.06)",
                  userSelect: "text",
                }}
                data-testid="lead-magnet-post-block"
              >
                {poszt.body || ""}
                {/* Kis copy ikon a JOBB ALSÓ sarokban (mint az email-generátornál). */}
                <button
                  type="button"
                  onClick={handleCopyPost}
                  data-testid="lead-magnet-copy-post"
                  title={copiedPost ? "Másolva" : "Másolás"}
                  aria-label={copiedPost ? "Másolva" : "Másolás"}
                  style={{
                    position: "absolute",
                    right: 12, bottom: 12,
                    width: 32, height: 32,
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    background: copiedPost ? "rgba(38,169,201,0.14)" : "transparent",
                    border: `1px solid ${copiedPost ? "rgba(38,169,201,0.45)" : "rgba(10,10,18,0.10)"}`,
                    color: copiedPost ? "#135b6c" : "#6c6c7a",
                    borderRadius: 8,
                    cursor: poszt.body ? "pointer" : "not-allowed",
                    transition: "background 140ms ease, border-color 140ms ease, color 140ms ease",
                    fontFamily: "inherit",
                  }}
                  onMouseEnter={(e) => {
                    if (!copiedPost && poszt.body) {
                      e.currentTarget.style.background = "rgba(10,10,18,0.045)";
                      e.currentTarget.style.borderColor = "rgba(10,10,18,0.18)";
                      e.currentTarget.style.color = "#1a1a22";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!copiedPost) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.borderColor = "rgba(10,10,18,0.10)";
                      e.currentTarget.style.color = "#6c6c7a";
                    }
                  }}
                  disabled={!poszt.body}
                >
                  {copiedPost
                    ? <CheckCircle size={15} weight="bold" />
                    : <CopyIcon size={15} weight="regular" />}
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
